import React, { useCallback, useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { Button } from '../ui/button';

type ThemeChoice = 'light' | 'dark';

const STORAGE_KEY = 'theme';

const applyEffective = (effective: ThemeChoice): void => {
  document.documentElement.classList.toggle('dark', effective === 'dark');
};

const readChoice = (): ThemeChoice => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  // First visit (no explicit choice): default to Light regardless of the OS
  // preference. After the first toggle the choice is persisted.
  return 'light';
};

const LABELS: Record<ThemeChoice, string> = {
  light: 'Light',
  dark: 'Dark',
};

/**
 * Two-state theme control (Light / Dark).
 *
 * - First visit defaults to Light (OS preference is not consulted).
 * - The button toggles Light <-> Dark and persists the explicit choice.
 * - The pre-paint class is set by an inline script in index.html, so there is no
 *   flash of the wrong theme on load; this component keeps it in sync afterwards.
 */
export const ThemeToggle: React.FC = () => {
  const [choice, setChoice] = useState<ThemeChoice>('light');

  // Initialise from storage (Light on first visit) on mount.
  useEffect(() => {
    const initial = readChoice();
    setChoice(initial);
    applyEffective(initial);
  }, []);

  const cycle = useCallback(() => {
    setChoice((prev) => {
      const next: ThemeChoice = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(STORAGE_KEY, next);
      applyEffective(next);
      return next;
    });
  }, []);

  const nextChoice: ThemeChoice = choice === 'dark' ? 'light' : 'dark';
  const Icon = choice === 'light' ? Sun : Moon;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={cycle}
      className="h-11 min-h-[44px] w-11 min-w-[44px] rounded-r-lg rounded-l-none border-0"
      aria-label="Toggle theme"
      title={`Theme: ${LABELS[choice]} · switch to ${LABELS[nextChoice]}`}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
};

export default ThemeToggle;
